import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import '../../config/api';
import { parseAmountShorthand } from '../../utils/amountUtils';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';

/**
 * State and calls for the Trade Builder's managed-CLO lane (design §3.5). The builder screen
 * renders it with its own sections and styles; nothing visual lives here.
 */

export interface LoanSecurity { id: number; cusip: string; securityName?: string; currentPrice?: number | null }

export interface ManagedPortfolio {
  portfolioId: number; portfolioName: string; portfolioCode?: string; currency?: string; ruleCount: number;
  dealId?: number | null; dealName?: string | null; noteClasses?: number | null;
  legalBuyer?: string | null; missingTests?: string[]; disabledTests?: string[];
}

export interface RuleRow {
  ruleId: number | null; ruleKey: string; ruleName: string; className?: string | null; unit?: string | null;
  before?: number | null; after?: number | null; limit?: number | null; passed: boolean | null; status: string;
  breachAction?: string | null; message?: string | null; calculation?: string | null; missing: string[];
}

export interface Capacity { status: string; minimum?: number | null; maximum?: number | null; binding: string[]; nextSize?: number | null; nextFailures: string[]; reasons: string[] }
export interface Economics { clean: number; accrued: number; fee: number; other: number; total: number }

export interface PortfolioResult {
  portfolioId: number; portfolioName: string; portfolioCode?: string | null; dealName?: string | null; noteClasses?: number | null;
  managed: boolean; state: string; proposedPar: number; targetWeight?: number | null; capacity: Capacity;
  cashBefore?: number | null; cashAfter?: number | null; economics: Economics; compliant: boolean | null;
  rules: RuleRow[]; errors: string[]; warnings: string[]; issues: string[]; bookHash?: string | null;
  legalBuyer?: string | null; custody?: string | null; missingTests?: string[] | null; disabledTests?: string[] | null;
  draftCount?: number | null; draftPar?: number | null;
}

/** What one test's number is made of: a generic table the engine describes. */
export interface Contributions {
  ruleId: number; ruleName?: string; portfolioName?: string; title: string; coverage?: string | null;
  columns: { key: string; label: string; unit: string }[]; rows: Record<string, any>[];
}

export interface Analysis {
  engine: string; method: string; securityId: number; cusip: string; securityName?: string; tradeDate: string; settleDate: string;
  settlementConvention?: string; cleanPrice: number; accruedPerPar: number; assignmentFee: number; assignmentFeePayer: string; otherFees: number;
  sellerCounterparty?: string | null; transferForm: string; requestedPar: number; increment: number; minimumTicket: number;
  portfolios: PortfolioResult[]; allocatedPar: number; residualPar: number; totals: Economics; inputHash: string; label?: string | null;
  bookingMode?: 'BLOCK' | 'SINGLE' | 'NONE' | null; fundedPortfolios?: number | null; blockFee?: number | null;
  weightBasis?: WeightBasis | null;
}

export interface Saved { id: string; inputHash: string; createdBy: string; createdAt: string; analysis: Analysis; existing: boolean }
export interface Ticket { portfolioId: number | null; portfolioName?: string | null; tradeId: number; status: string; par: number; sourceRef: string; created: boolean; blockRole?: string | null; parentTradeId?: number | null }
export interface RecentRun { id: string; label?: string | null; createdBy: string; createdAt: string; requestedPar?: string; allocatedPar?: string; tickets: number }
export type Method = 'ABSOLUTE' | 'PRO_RATA' | 'SIZE_TO_CAPACITY';
/** How a size-to-capacity request is split: by each CLO's capacity (the engine decides) or by weights the user sets. */
export type WeightBasis = 'CAPACITY' | 'TARGET';
export interface Fact { portfolio?: string | null; kind: string; text: string }
export interface Explanation { facts: Fact[]; summary: string; narrative?: string | null; llm?: string | null; llmError?: string | null }
/** What the builder's natural-language parser extracts for a loan purchase. */
export interface LoanIntent {
  price?: number | null; accruedPerPar?: number | null; assignmentFee?: number | null; seller?: string | null;
  amount?: number | null; amountType?: string | null; allocationMethod?: string | null;
  targetWeights?: Record<string, number> | null; portfolioPars?: Record<string, number> | null; portfolioIds?: number[] | null;
}

export interface LoanTerms {
  cleanPrice: string; accruedPerPar: string; assignmentFee: string; assignmentFeePayer: string; otherFees: string;
  sellerCounterparty: string; transferForm: string; tradeDate: string; settleDate: string; settlementConvention: string;
  requestedPar: string; increment: string; minimumTicket: string; label: string;
}

export const money = (v: number | null | undefined, digits = 0) =>
  v == null || Number.isNaN(Number(v)) ? '—' : `${Number(v) < 0 ? '-' : ''}$${Math.abs(Number(v)).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
export const parShort = (v: number | null | undefined) => (v == null ? '—' : Number(v) >= 1_000_000 ? `$${(Number(v) / 1_000_000).toFixed(2)}MM` : money(v));
export const showValue = (v: number | null | undefined, unit?: string | null) => {
  if (v == null) return '—';
  const n = Number(v);
  switch (unit) {
    case '%': return `${n.toFixed(2)}%`; case 'bps': return `${n.toFixed(1)} bps`; case 'years': return `${n.toFixed(2)} yrs`;
    case 'factor': return n.toFixed(0); case 'USD': return money(n); case 'x': return `${n.toFixed(3)}x`; case 'score': return n.toFixed(2);
    default: return String(n);
  }
};
export const stateText: Record<string, string> = {
  CLEAR: 'All tests pass', BREACH: 'Breach', NO_CAPACITY: 'No capacity', RULEBOOK_INCOMPLETE: 'Rulebook incomplete', ASSESSED_WITH_GAPS: 'Open tests', NOT_ASSESSABLE: 'Not assessable', NOT_MANAGED: 'Not a managed CLO', UNVALIDATED: 'Unvalidated',
};
export const rowResult = (r: RuleRow) => (r.passed ? 'PASS' : r.status === 'UNKNOWN' ? 'UNKNOWN' : r.status === 'NOT_APPLICABLE' ? 'N/A' : r.breachAction === 'BLOCK' ? 'FAIL' : r.breachAction || 'FAIL');

const BASE = '/api/portfolio-construction/clo';

export function useCloLane(security: LoanSecurity | null, initialPortfolioId: number | null | undefined) {
  const [managed, setManaged] = useState<ManagedPortfolio[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [method, setMethod] = useState<Method>('SIZE_TO_CAPACITY');
  const [lines, setLines] = useState<Record<number, { par: string; weight: string }>>({});
  const [weightBasis, setWeightBasis] = useState<WeightBasis>('CAPACITY');
  const [terms, setTerms] = useState<LoanTerms>({
    cleanPrice: '', accruedPerPar: '0', assignmentFee: '3,500', assignmentFeePayer: 'BUYER', otherFees: '0', sellerCounterparty: '',
    transferForm: 'ASSIGNMENT', tradeDate: '', settleDate: '', settlementConvention: '', requestedPar: '', increment: '100,000', minimumTicket: '100,000', label: '',
  });
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [freshness, setFreshness] = useState<any>(null);
  const [recent, setRecent] = useState<RecentRun[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [explanation, setExplanation] = useState<Explanation | null>(null);
  // Drill-downs by "portfolioId:ruleId": what a test's number is made of, fetched when a row is opened.
  const [drill, setDrill] = useState<Record<string, Contributions | 'loading' | { error: string }>>({});
  const [excludedFromGroup, setExcludedFromGroup] = useState<string[]>([]);

  const securityId = security?.id ?? null;
  const set = useCallback((key: keyof LoanTerms, v: string) => setTerms(prev => ({ ...prev, [key]: v })), []);
  const reset = useCallback(() => { setAnalysis(null); setSaved(null); setTickets(null); setFreshness(null); setError(null); setExplanation(null); setDrill({}); }, []);

  // A new loan: price from the mark when there is one, a clean slate otherwise.
  useEffect(() => {
    if (!securityId) return;
    setTerms(prev => ({ ...prev, cleanPrice: security?.currentPrice ? String(security.currentPrice) : prev.cleanPrice,
      tradeDate: prev.tradeDate || getTodayDate(calendarsFor('LEVERAGED_LOAN', 'USD')) }));
    reset();
    axios.get(`${BASE}/managed-portfolios`).then(res => {
      const list: ManagedPortfolio[] = Array.isArray(res.data) ? res.data : [];
      setManaged(list);
      setSelected(initialPortfolioId && list.some(p => p.portfolioId === initialPortfolioId) ? [initialPortfolioId] : list.map(p => p.portfolioId));
    }).catch(err => setError(err.response?.data?.error || err.response?.data?.message || 'Could not load the managed CLOs'));
    axios.get(`${BASE}/runs`, { params: { securityId, limit: 8 } }).then(res => setRecent(Array.isArray(res.data) ? res.data : [])).catch(() => setRecent([]));
  }, [securityId, initialPortfolioId, security?.currentPrice, reset]);

  // LSTA settlement from the backend convention, never typed here.
  useEffect(() => {
    const price = parseAmountShorthand(terms.cleanPrice);
    if (!terms.tradeDate || !Number.isFinite(price) || price <= 0) return;
    const tradeDate = terms.tradeDate;
    axios.get('/api/credit/ll/settlement-convention', { params: { tradeDate, cleanPrice: price } })
      .then(res => setTerms(prev => (prev.tradeDate === tradeDate ? { ...prev, settleDate: res.data.expectedSettlementDate, settlementConvention: res.data.convention } : prev)))
      .catch(() => undefined);
  }, [terms.tradeDate, terms.cleanPrice]);

  const toggle = useCallback((id: number) => setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])), []);
  const line = useCallback((id: number) => lines[id] || { par: '', weight: '1' }, [lines]);
  const setLine = useCallback((id: number, key: 'par' | 'weight', v: string) => setLines(prev => ({ ...prev, [id]: { ...(prev[id] || { par: '', weight: '1' }), [key]: v } })), []);

  const request = useCallback(() => ({
    securityId,
    method,
    tradeDate: terms.tradeDate,
    settleDate: terms.settleDate || null,
    cleanPrice: parseAmountShorthand(terms.cleanPrice),
    accruedPerPar: Number(String(terms.accruedPerPar).replace(/,/g, '') || 0),
    assignmentFee: parseAmountShorthand(terms.assignmentFee) || 0,
    assignmentFeePayer: terms.assignmentFeePayer,
    otherFees: parseAmountShorthand(terms.otherFees) || 0,
    sellerCounterparty: terms.sellerCounterparty || null,
    transferForm: terms.transferForm,
    requestedPar: method === 'ABSOLUTE' ? null : parseAmountShorthand(terms.requestedPar),
    increment: parseAmountShorthand(terms.increment) || 100000,
    minimumTicket: parseAmountShorthand(terms.minimumTicket) || null,
    label: terms.label || null,
    weightBasis: method === 'SIZE_TO_CAPACITY' ? weightBasis : null,
    allocations: selected.map(id => ({
      portfolioId: id,
      par: method === 'ABSOLUTE' ? parseAmountShorthand(line(id).par) : null,
      targetWeight: method === 'SIZE_TO_CAPACITY' && weightBasis === 'TARGET' ? Number(line(id).weight || 0) : null,
    })),
  }), [securityId, method, terms, selected, line, weightBasis]);

  const fail = (err: any, fallback: string) => setError(err.response?.data?.message || err.response?.data?.error || err.message || fallback);

  const refreshTickets = useCallback(async (runId: string) => {
    const ex = await axios.get(`${BASE}/runs/${runId}/tickets`);
    setTickets(Array.isArray(ex.data) && ex.data.length ? ex.data : null);
  }, []);

  const analyze = useCallback(async () => {
    setError(null); setBusy('Sizing against each CLO’s indenture rules…');
    try {
      const res = await axios.post(`${BASE}/analyze`, request());
      setAnalysis(res.data); setSaved(null); setTickets(null); setFreshness(null); setDrill({});
    } catch (err: any) { fail(err, 'Analysis failed'); } finally { setBusy(null); }
  }, [request]);

  const save = useCallback(async () => {
    setError(null); setBusy('Saving the analysis…');
    try {
      const res = await axios.post(`${BASE}/runs`, request());
      setSaved(res.data); setAnalysis(res.data.analysis);
      await refreshTickets(res.data.id);
      const runs = await axios.get(`${BASE}/runs`, { params: { securityId, limit: 8 } });
      setRecent(Array.isArray(runs.data) ? runs.data : []);
    } catch (err: any) { fail(err, 'Could not save the analysis'); } finally { setBusy(null); }
  }, [request, refreshTickets, securityId]);

  const load = useCallback(async (id: string) => {
    setError(null); setBusy('Loading the saved analysis…');
    try {
      const res = await axios.get(`${BASE}/runs/${id}`);
      const a: Analysis = res.data.analysis;
      setSaved(res.data); setAnalysis(a);
      setMethod(a.method as Method);
      setWeightBasis(a.weightBasis === 'TARGET' ? 'TARGET' : 'CAPACITY');
      setSelected(a.portfolios.map(p => p.portfolioId));
      const fmt = (n: number) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 6 });
      setTerms(prev => ({ ...prev, cleanPrice: String(a.cleanPrice), accruedPerPar: String(a.accruedPerPar), assignmentFee: fmt(a.assignmentFee),
        assignmentFeePayer: a.assignmentFeePayer, otherFees: fmt(a.otherFees), sellerCounterparty: a.sellerCounterparty || '', transferForm: a.transferForm,
        tradeDate: a.tradeDate, settleDate: a.settleDate, settlementConvention: a.settlementConvention || '', requestedPar: fmt(a.requestedPar),
        increment: fmt(a.increment), minimumTicket: fmt(a.minimumTicket), label: a.label || '' }));
      const next: Record<number, { par: string; weight: string }> = {};
      a.portfolios.forEach(p => { next[p.portfolioId] = { par: fmt(p.proposedPar), weight: p.targetWeight == null ? '1' : String(p.targetWeight) }; });
      setLines(next);
      const [, fr] = await Promise.all([refreshTickets(id), axios.get(`${BASE}/runs/${id}/freshness`)]);
      setFreshness(fr.data);
    } catch (err: any) { fail(err, 'Could not load the analysis'); } finally { setBusy(null); }
  }, [refreshTickets]);

  const checkFreshness = useCallback(async () => {
    if (!saved) return;
    setBusy('Comparing the live book to the analysis…');
    try { const res = await axios.get(`${BASE}/runs/${saved.id}/freshness`); setFreshness(res.data); }
    catch (err: any) { fail(err, 'Freshness check failed'); } finally { setBusy(null); }
  }, [saved]);

  /** Books the analysis; saves it first when it has not been saved, since the trade references the saved run. */
  const createTrade = useCallback(async () => {
    setError(null);
    // A secondary assignment names the legal seller on the confirm; the ticket lane refuses one without it.
    const needsSeller = terms.transferForm === 'ASSIGNMENT';
    if (needsSeller && !terms.sellerCounterparty.trim()) {
      setError('Enter the seller of record in Loan Terms. An assignment cannot be booked without the legal seller.');
      return;
    }
    try {
      // A seller typed after the analysis was saved: the saved analysis is immutable, so save again with it.
      let run = saved && needsSeller && !(saved.analysis.sellerCounterparty || '').trim() ? null : saved;
      if (!run) {
        setBusy('Saving the analysis…');
        const res = await axios.post(`${BASE}/runs`, request());
        run = res.data; setSaved(run); setAnalysis(res.data.analysis);
      }
      setBusy('Booking the trade through the ticket lane…');
      const res = await axios.post(`${BASE}/runs/${run!.id}/tickets`); setTickets(res.data);
      const runs = await axios.get(`${BASE}/runs`, { params: { securityId, limit: 8 } });
      setRecent(Array.isArray(runs.data) ? runs.data : []);
    } catch (err: any) { fail(err, 'Could not create the trade'); } finally { setBusy(null); }
  }, [saved, request, securityId, terms.transferForm, terms.sellerCounterparty]);

  const exportRun = useCallback(async (format: 'csv' | 'json') => {
    if (!saved) return;
    try {
      const res = await axios.get(`${BASE}/runs/${saved.id}/export`, { params: { format }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `builder-run-${saved.id}.${format}`; a.click(); URL.revokeObjectURL(url);
    } catch (err: any) { fail(err, 'Export failed'); }
  }, [saved]);

  /** A portfolio group drives the selection: its managed members are chosen, the rest are named as excluded. */
  const selectGroup = useCallback((group: any) => {
    const members: any[] = Array.isArray(group?.portfolios) ? group.portfolios : [];
    const ids = members.map(m => Number(m.id ?? m.portfolioId));
    const chosen = managed.filter(p => ids.includes(p.portfolioId)).map(p => p.portfolioId);
    setSelected(chosen);
    setExcludedFromGroup(members.filter(m => !chosen.includes(Number(m.id ?? m.portfolioId))).map(m => m.portfolioName || m.name || `portfolio ${m.id}`));
  }, [managed]);

  /** Saves the current selection as a portfolio group, so the next analysis is one click. */
  const saveAsGroup = useCallback(async (groupName: string) => {
    const created = await axios.post('/api/portfolio-groups', { groupName, description: 'Managed CLOs sized together in the Portfolio Builder' });
    const id = created.data?.id;
    if (id) await axios.post(`/api/portfolio-groups/${id}/portfolios`, { portfolioIds: selected });
    return created.data;
  }, [selected]);

  /** The builder's natural-language parser filled these; apply what names a loan purchase. */
  const applyIntent = useCallback((intent: LoanIntent) => {
    const fmt = (n: number) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 6 });
    setTerms(prev => ({ ...prev,
      cleanPrice: intent.price != null ? String(intent.price) : prev.cleanPrice,
      accruedPerPar: intent.accruedPerPar != null ? String(intent.accruedPerPar) : prev.accruedPerPar,
      assignmentFee: intent.assignmentFee != null ? fmt(intent.assignmentFee) : prev.assignmentFee,
      sellerCounterparty: intent.seller || prev.sellerCounterparty,
      requestedPar: intent.amount != null && intent.amountType !== 'PERCENT' ? fmt(intent.amount) : prev.requestedPar,
    }));
    const m = String(intent.allocationMethod || '').toUpperCase();
    const pars = intent.portfolioPars || null;
    // Weights in the command are the user's; none means the engine splits by capacity.
    setWeightBasis(intent.targetWeights && Object.keys(intent.targetWeights).length > 0 ? 'TARGET' : 'CAPACITY');
    if (pars && Object.keys(pars).length > 0) setMethod('ABSOLUTE');
    else if (m === 'PRO_RATA') setMethod('PRO_RATA');
    else setMethod('SIZE_TO_CAPACITY');
    const byName = (name: string) => managed.find(p => p.portfolioName.toLowerCase().includes(name.toLowerCase()) || name.toLowerCase().includes(p.portfolioName.toLowerCase()));
    const next: Record<number, { par: string; weight: string }> = {};
    const chosen: number[] = [];
    Object.entries(intent.targetWeights || {}).forEach(([name, w]) => { const p = byName(name); if (p) { chosen.push(p.portfolioId); next[p.portfolioId] = { par: '', weight: String(w) }; } });
    Object.entries(pars || {}).forEach(([name, par]) => { const p = byName(name); if (p) { chosen.push(p.portfolioId); next[p.portfolioId] = { par: fmt(par), weight: next[p.portfolioId]?.weight || '1' }; } });
    (intent.portfolioIds || []).forEach(id => { if (managed.some(p => p.portfolioId === id) && !chosen.includes(id)) chosen.push(id); });
    if (chosen.length > 0) { setSelected(chosen); setLines(prev => ({ ...prev, ...next })); }
  }, [managed]);

  const explain = useCallback(async () => {
    if (!analysis) return;
    setBusy('Explaining the sizing…');
    try { const res = await axios.post(`${BASE}/assistant/explain`, analysis); setExplanation(res.data); }
    catch (err: any) { fail(err, 'Could not explain the sizing'); } finally { setBusy(null); }
  }, [analysis]);

  /** Opens or closes the drill-down for one test on one CLO. */
  const toggleDrill = useCallback(async (portfolioId: number, ruleId: number | null, par: number) => {
    if (ruleId == null) return;
    const key = `${portfolioId}:${ruleId}`;
    if (drill[key]) { setDrill(prev => { const next = { ...prev }; delete next[key]; return next; }); return; }
    setDrill(prev => ({ ...prev, [key]: 'loading' }));
    try {
      const res = await axios.post(`${BASE}/contributions`, { request: request(), portfolioId, ruleId, par });
      setDrill(prev => ({ ...prev, [key]: res.data }));
    } catch (err: any) {
      setDrill(prev => ({ ...prev, [key]: { error: err.response?.data?.message || err.response?.data?.error || 'Could not load the detail' } }));
    }
  }, [drill, request]);

  /** Compact facts for the builder's chat assistant, so questions are answered from the engine's numbers. */
  const chatContext = useMemo(() => {
    if (!analysis) return null;
    const lines: string[] = [`${analysis.securityName || analysis.cusip}: ${analysis.method} ${analysis.requestedPar} requested at ${analysis.cleanPrice}; allocated ${analysis.allocatedPar}, residual ${analysis.residualPar}, cash ${analysis.totals.total}.`];
    analysis.portfolios.forEach(p => {
      lines.push(`${p.portfolioName}: ${stateText[p.state] || p.state}; par ${p.proposedPar}; capacity ${p.capacity.maximum ?? p.capacity.status}${p.capacity.binding.length ? ' bound by ' + p.capacity.binding.join(', ') : ''}; cash ${p.cashBefore} -> ${p.cashAfter}.`);
      p.rules.filter(r => r.ruleKey.startsWith('clo_')).forEach(r => lines.push(`  ${r.ruleName}${r.className ? ' ' + r.className : ''}: ${r.before ?? '—'} -> ${r.after ?? '—'} ${r.unit || ''} limit ${r.limit ?? '—'} ${rowResult(r)}${r.missing.length ? ' missing ' + r.missing.join(',') : ''}`));
    });
    return { summary: lines.join('\n') };
  }, [analysis]);

  const ready = !!securityId && selected.length > 0 && parseAmountShorthand(terms.cleanPrice) > 0
    && (method === 'ABSOLUTE' ? selected.some(id => parseAmountShorthand(line(id).par) > 0) : parseAmountShorthand(terms.requestedPar) > 0);
  const funded = useMemo(() => (analysis?.portfolios || []).filter(p => p.proposedPar > 0), [analysis]);
  const parent = useMemo(() => (tickets || []).find(t => t.blockRole === 'BLOCK_PARENT') || null, [tickets]);
  const allocations = useMemo(() => (tickets || []).filter(t => t.blockRole !== 'BLOCK_PARENT'), [tickets]);

  return {
    managed, selected, setSelected, toggle, method, setMethod, line, setLine, terms, set, analysis, saved, tickets, parent, allocations,
    freshness, recent, busy, error, setError, ready, funded, analyze, save, load, checkFreshness, createTrade, exportRun, reset,
    explanation, explain, chatContext, selectGroup, saveAsGroup, excludedFromGroup, applyIntent, weightBasis, setWeightBasis,
    drill, toggleDrill,
  };
}
